import React from 'react';
import PanToolAltOutlinedIcon from '@mui/icons-material/PanToolAltOutlined';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Snackbar,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { getAcceptLanguageHeader, useI18n } from '../i18n';

const CURRENCIES = [
  { code: 'USD' },
  { code: 'RUB' },
  { code: 'EUR' },
  { code: 'TRY' },
  { code: 'CNY' },
  { code: 'KZT' },
];

const SERVICES = [
  { code: 'TMCELL', label: 'TMCELL' },
  { code: 'TTELECOM', label: 'TTelecom' },
  { code: 'ASTUINT', label: 'ASTUint' },
  { code: 'BELET', label: 'Belet' },
  { code: 'GB_PACKET_1_5', label: 'GB Packet 1.5' },
  { code: 'GB_PACKET_4', label: 'GB Packet 4' },
  { code: 'GB_PACKET_20', label: 'GB Packet 20' },
];

const TELECOM_LINK_SERVICES = new Set(['ASTUINT', 'BELET']);

const PAYMENT_SYSTEM_TYPES = [
  { value: 'sbp', label: 'СБП' },
  { value: 'cards', label: 'Карты' },
  { value: 'other', label: 'Другое' },
];

const PAYMENT_SYSTEM_LANG_FIELDS = [
  { field: 'name_ru', label: 'Название RU' },
  { field: 'name_en', label: 'Название EN' },
  { field: 'name_tm', label: 'Название TM' },
  { field: 'name_tr', label: 'Название TR' },
];

const normalizeServiceCode = (value) => {
  const service = String(value || '').toUpperCase();
  return service === 'BELET_FILM' ? 'BELET' : service;
};

const normalizePaymentSystemCode = (value) => String(value || '').trim().toLowerCase();

const normalizeEnabledFlag = (value) => {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'number') {
    return value !== 0;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized !== '' && normalized !== '0' && normalized !== 'false';
  }
  return true;
};

const createEmptyPaymentSystem = () => ({
  local_id: `new-${Date.now()}-${Math.random().toString(36).slice(2)}`,
  code: '',
  name_ru: '',
  name_en: '',
  name_tm: '',
  name_tr: '',
  enabled: true,
  image_url: '',
  type: 'other',
  weight: '1',
});

const buildEmptyRates = () => {
  const initial = {};
  SERVICES.forEach(({ code: service }) => {
    initial[service] = {};
    CURRENCIES.forEach(({ code }) => {
      initial[service][code] = '';
    });
  });
  return initial;
};

const buildInitialEnabledServices = () => (
  SERVICES.reduce((acc, { code }) => {
    acc[code] = true;
    return acc;
  }, {})
);

const buildInitialManualModeByService = () => (
  SERVICES.reduce((acc, { code }) => {
    acc[code] = false;
    return acc;
  }, {})
);

export default function PaymentFormPage() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [tab, setTab] = React.useState(0);
  const [loading, setLoading] = React.useState(true);
  const [saving, setSaving] = React.useState(false);
  const [loadingPaymentSystems, setLoadingPaymentSystems] = React.useState(false);
  const [savingPaymentSystems, setSavingPaymentSystems] = React.useState(false);
  const [rates, setRates] = React.useState(buildEmptyRates);
  const [paymentSystems, setPaymentSystems] = React.useState([]);
  const [linkedToTelecom, setLinkedToTelecom] = React.useState({
    ASTUINT: false,
    BELET: false,
  });
  const [enabledServices, setEnabledServices] = React.useState(buildInitialEnabledServices);
  const [manualModeByService, setManualModeByService] = React.useState(buildInitialManualModeByService);
  const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'success' });

  const showMessage = React.useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const normalizeValue = (value) => value.replace(',', '.');

  const loadRates = React.useCallback(() => {
    setLoading(true);
    fetch('/api/payment-form/rates', {
      method: 'GET',
      headers: { 'Accept-Language': getAcceptLanguageHeader() },
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(t('Ошибка загрузки курса'));
        return res.json();
      })
      .then((data) => {
        const items = Array.isArray(data?.items) ? data.items : [];
        const next = buildEmptyRates();
        const nextEnabled = buildInitialEnabledServices();
        const nextManualMode = buildInitialManualModeByService();
        items.forEach((item) => {
          const service = normalizeServiceCode(item.service);
          const code = String(item.currency || '').toUpperCase();
          if (next[service] && Object.prototype.hasOwnProperty.call(next[service], code)) {
            next[service][code] = item.rate != null ? String(item.rate) : '';
            nextEnabled[service] = normalizeEnabledFlag(item.enabled);
            nextManualMode[service] = normalizeEnabledFlag(item.manual_mode ?? 0);
          }
        });
        setRates(next);
        setEnabledServices(nextEnabled);
        setManualModeByService(nextManualMode);
      })
      .catch((err) => {
        showMessage(err.message || t('Ошибка загрузки'), 'error');
      })
      .finally(() => setLoading(false));
  }, [showMessage, t]);

  const loadPaymentSystems = React.useCallback(() => {
    if (!user?.api_token) {
      setLoadingPaymentSystems(false);
      return;
    }
    setLoadingPaymentSystems(true);
    fetch('/api/payment-form/payment-systems/admin', {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${user.api_token}`,
        'Accept-Language': getAcceptLanguageHeader(),
      },
    })
      .then(async (res) => {
        if (!res.ok) throw new Error(t('Ошибка загрузки платёжных систем'));
        return res.json();
      })
      .then((data) => {
        const items = Array.isArray(data?.items) ? data.items : [];
        setPaymentSystems(items.map((item) => ({
          id: item.id,
          local_id: item.id ? `id-${item.id}` : `code-${item.code}`,
          code: normalizePaymentSystemCode(item.code),
          name_ru: item.name_ru || '',
          name_en: item.name_en || '',
          name_tm: item.name_tm || '',
          name_tr: item.name_tr || '',
          enabled: normalizeEnabledFlag(item.enabled),
          image_url: item.image_url || '',
          type: PAYMENT_SYSTEM_TYPES.some(({ value }) => value === item.type) ? item.type : 'other',
          weight: item.weight != null ? String(item.weight) : '1',
        })));
      })
      .catch((err) => {
        showMessage(err.message || t('Ошибка загрузки'), 'error');
      })
      .finally(() => setLoadingPaymentSystems(false));
  }, [showMessage, t, user?.api_token]);

  React.useEffect(() => {
    loadRates();
  }, [loadRates]);

  React.useEffect(() => {
    if (tab === 1) {
      loadPaymentSystems();
    }
  }, [loadPaymentSystems, tab]);

  const handleRateChange = (service, code) => (event) => {
    const value = normalizeValue(event.target.value);
    setRates((prev) => {
      const next = {
        ...prev,
        [service]: {
          ...prev[service],
          [code]: value,
        },
      };
      if (service === 'TTELECOM') {
        Object.entries(linkedToTelecom).forEach(([linkedService, enabled]) => {
          if (enabled) {
            next[linkedService] = {
              ...next[linkedService],
              [code]: value,
            };
          }
        });
      }
      return next;
    });
  };

  const handleTelecomLinkToggle = (service) => (event) => {
    const checked = event.target.checked;
    setLinkedToTelecom((prev) => ({
      ...prev,
      [service]: checked,
    }));
    if (checked) {
      setRates((prev) => ({
        ...prev,
        [service]: {
          ...prev[service],
          ...CURRENCIES.reduce((acc, { code }) => {
            acc[code] = prev?.TTELECOM?.[code] ?? '';
            return acc;
          }, {}),
        },
      }));
    }
  };

  const handleServiceEnabledToggle = (service) => (event) => {
    const checked = event.target.checked;
    setEnabledServices((prev) => ({
      ...prev,
      [service]: checked,
    }));
  };

  const handleManualModeToggle = (service) => (event) => {
    const checked = event.target.checked;
    setManualModeByService((prev) => ({
      ...prev,
      [service]: checked,
    }));
  };

  const rateErrors = React.useMemo(() => {
    const errors = {};
    SERVICES.forEach(({ code: service }) => {
      errors[service] = {};
      CURRENCIES.forEach(({ code }) => {
        const raw = String(rates?.[service]?.[code] ?? '').trim();
        const value = parseFloat(raw);
        errors[service][code] = !raw || Number.isNaN(value) || value <= 0;
      });
    });
    return errors;
  }, [rates]);

  const hasErrors = Object.values(rateErrors).some((serviceErrors) =>
    Object.values(serviceErrors).some(Boolean)
  );

  const paymentSystemErrors = React.useMemo(() => {
    const codeCounts = paymentSystems.reduce((acc, item) => {
      const code = normalizePaymentSystemCode(item.code);
      if (code) {
        acc[code] = (acc[code] || 0) + 1;
      }
      return acc;
    }, {});

    return paymentSystems.map((item) => {
      const code = normalizePaymentSystemCode(item.code);
      const weight = Number(item.weight);
      return {
        code: !code || !/^[a-z0-9_-]+$/.test(code) || codeCounts[code] > 1,
        name_ru: !String(item.name_ru || '').trim(),
        name_en: !String(item.name_en || '').trim(),
        type: !PAYMENT_SYSTEM_TYPES.some(({ value }) => value === item.type),
        weight: !Number.isInteger(weight) || weight < 1 || weight > 100,
      };
    });
  }, [paymentSystems]);

  const hasPaymentSystemErrors = paymentSystemErrors.some((errors) =>
    Object.values(errors).some(Boolean)
  );

  const handlePaymentSystemChange = (index, field) => (event) => {
    const value = field === 'enabled' ? event.target.checked : event.target.value;
    setPaymentSystems((prev) => prev.map((item, itemIndex) => (
      itemIndex === index
        ? {
            ...item,
            [field]: field === 'code' ? normalizePaymentSystemCode(value) : value,
          }
        : item
    )));
  };

  const handleAddPaymentSystem = () => {
    setPaymentSystems((prev) => [...prev, createEmptyPaymentSystem()]);
  };

  const handleRemoveNewPaymentSystem = (index) => {
    setPaymentSystems((prev) => prev.filter((_, itemIndex) => itemIndex !== index));
  };

  const handleSave = async () => {
    if (!user?.api_token) {
      showMessage(t('Нет доступа'), 'error');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        items: SERVICES.flatMap(({ code: service }) =>
          CURRENCIES.map(({ code }) => ({
            service,
            currency: code,
            rate: parseFloat(String(rates?.[service]?.[code]).trim()),
            enabled: Boolean(enabledServices?.[service]),
            manual_mode: Boolean(manualModeByService?.[service]),
          }))
        ),
      };
      const res = await fetch('/api/payment-form/rates', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.api_token}`,
          'Accept-Language': getAcceptLanguageHeader(),
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || t('Ошибка сохранения'));
      }
      showMessage(t('Курс сохранён'), 'success');
      loadRates();
    } catch (err) {
      showMessage(err.message || t('Ошибка сохранения'), 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleSavePaymentSystems = async () => {
    if (!user?.api_token) {
      showMessage(t('Нет доступа'), 'error');
      return;
    }
    if (hasPaymentSystemErrors) {
      showMessage(t('Проверьте поля платёжных систем'), 'error');
      return;
    }
    setSavingPaymentSystems(true);
    try {
      const payload = {
        items: paymentSystems.map((item) => ({
          code: normalizePaymentSystemCode(item.code),
          name_ru: String(item.name_ru || '').trim(),
          name_en: String(item.name_en || '').trim(),
          name_tm: String(item.name_tm || '').trim(),
          name_tr: String(item.name_tr || '').trim(),
          enabled: Boolean(item.enabled),
          image_url: String(item.image_url || '').trim(),
          type: item.type || 'other',
          weight: parseInt(item.weight, 10),
        })),
      };
      const res = await fetch('/api/payment-form/payment-systems', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${user.api_token}`,
          'Accept-Language': getAcceptLanguageHeader(),
        },
        body: JSON.stringify(payload),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || t('Ошибка сохранения платёжных систем'));
      }
      showMessage(t('Платёжные системы сохранены'), 'success');
      loadPaymentSystems();
    } catch (err) {
      showMessage(err.message || t('Ошибка сохранения'), 'error');
    } finally {
      setSavingPaymentSystems(false);
    }
  };

  return (
    <Box sx={{ fontFamily: '"Google Sans Flex", "Google Sans", "Helvetica Neue", sans-serif' }}>
      <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>{t('Форма оплаты')}</Typography>
      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 3 }}>
        <Tab label={t('Курс TMT')} />
        <Tab label={t('Платёжные системы')} />
      </Tabs>
      {tab === 0 && (
        <Box sx={{ p: 3, borderRadius: 3, bgcolor: '#fff', width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{t('Курс 1 TMT')}</Typography>
              <Typography variant="body2" color="text.secondary">{t('Цена за 1 TMT в выбранной валюте')}</Typography>
            </Box>
            <Button variant="outlined" onClick={loadRates} disabled={loading}>
              {t('Обновить')}
            </Button>
          </Box>
          {SERVICES.map(({ code: service, label }) => (
            <Box key={service} sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1, flexWrap: 'wrap' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                  <Switch
                    checked={Boolean(enabledServices?.[service])}
                    onChange={handleServiceEnabledToggle(service)}
                    inputProps={{ 'aria-label': `${label} enabled` }}
                    size="small"
                  />
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{label}</Typography>
                  {TELECOM_LINK_SERVICES.has(service) && (
                    <FormControlLabel
                      sx={{ ml: 0 }}
                      control={
                        <Checkbox
                          checked={Boolean(linkedToTelecom?.[service])}
                          onChange={handleTelecomLinkToggle(service)}
                          size="small"
                        />
                      }
                      label={t('как TTelecom')}
                    />
                  )}
                  <FormControlLabel
                    sx={{
                      ml: 0.5,
                      mr: 0,
                      px: 1,
                      py: 0.25,
                      border: '1px solid',
                      borderColor: 'divider',
                      borderRadius: 999,
                      bgcolor: 'grey.50',
                      '.MuiFormControlLabel-label': {
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        fontSize: 13,
                        fontWeight: 500,
                      },
                      '.MuiSwitch-root': {
                        mr: 0.5,
                      },
                    }}
                    control={(
                      <Switch
                        checked={Boolean(manualModeByService?.[service])}
                        onChange={handleManualModeToggle(service)}
                        inputProps={{ 'aria-label': `${label} manual mode` }}
                        size="small"
                      />
                    )}
                    label={(
                      <>
                        <PanToolAltOutlinedIcon sx={{ fontSize: 16 }} />
                        {t('Manual')}
                      </>
                    )}
                  />
                </Box>
              </Box>
              <Grid container spacing={2}>
                {CURRENCIES.map(({ code }) => (
                  <Grid item xs={12} sm={6} md={4} key={`${service}-${code}`}>
                    <TextField
                      label={code}
                      value={rates?.[service]?.[code] ?? ''}
                      onChange={handleRateChange(service, code)}
                      type="number"
                      inputProps={{ step: '0.0001', min: '0' }}
                      error={Boolean(rateErrors?.[service]?.[code])}
                      helperText={rateErrors?.[service]?.[code] ? t('Введите положительное число') : ' '}
                      InputProps={{ endAdornment: <InputAdornment position="end">{code}</InputAdornment> }}
                      fullWidth
                    />
                  </Grid>
                ))}
              </Grid>
            </Box>
          ))}
          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button variant="contained" onClick={handleSave} disabled={saving || loading || hasErrors}>
              {saving ? t('Сохранение...') : t('Сохранить')}
            </Button>
          </Box>
        </Box>
      )}
      {tab === 1 && (
        <Box sx={{ p: 3, borderRadius: 3, bgcolor: '#fff', width: '100%' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2, mb: 2 }}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>{t('Платёжные системы')}</Typography>
              <Typography variant="body2" color="text.secondary">
                {t('Настройте способы оплаты, которые будут доступны в публичной форме')}
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button variant="outlined" onClick={loadPaymentSystems} disabled={loadingPaymentSystems}>
                {t('Обновить')}
              </Button>
              <Button variant="contained" onClick={handleAddPaymentSystem}>
                {t('Добавить систему')}
              </Button>
            </Box>
          </Box>

          {paymentSystems.length === 0 && !loadingPaymentSystems && (
            <Alert severity="info" sx={{ mb: 2 }}>{t('Нет платёжных систем')}</Alert>
          )}

          {paymentSystems.map((system, index) => {
            const errors = paymentSystemErrors[index] || {};
            const canRemove = !system.id;
            return (
              <Paper key={system.id || system.local_id || `${system.code}-${index}`} variant="outlined" sx={{ p: 2, mb: 2, borderRadius: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 2, flexWrap: 'wrap' }}>
                  <FormControlLabel
                    control={(
                      <Switch
                        checked={Boolean(system.enabled)}
                        onChange={handlePaymentSystemChange(index, 'enabled')}
                        size="small"
                      />
                    )}
                    label={t('Включена')}
                  />
                  {canRemove && (
                    <Button color="inherit" onClick={() => handleRemoveNewPaymentSystem(index)}>
                      {t('Убрать')}
                    </Button>
                  )}
                </Box>

                <Grid container spacing={2}>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label={t('Код')}
                      value={system.code}
                      onChange={handlePaymentSystemChange(index, 'code')}
                      error={Boolean(errors.code)}
                      helperText={errors.code ? t('Код обязателен, уникален и может содержать a-z, 0-9, _ или -') : t('Например: sbp')}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      select
                      label={t('Тип')}
                      value={system.type}
                      onChange={handlePaymentSystemChange(index, 'type')}
                      error={Boolean(errors.type)}
                      helperText={errors.type ? t('Выберите тип') : ' '}
                      fullWidth
                    >
                      {PAYMENT_SYSTEM_TYPES.map(({ value, label }) => (
                        <MenuItem key={value} value={value}>{t(label)}</MenuItem>
                      ))}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={4}>
                    <TextField
                      label={t('Вес')}
                      value={system.weight}
                      onChange={handlePaymentSystemChange(index, 'weight')}
                      type="number"
                      inputProps={{ min: 1, max: 100, step: 1 }}
                      error={Boolean(errors.weight)}
                      helperText={errors.weight ? t('Введите число от 1 до 100') : t('Для будущего распределения')}
                      fullWidth
                    />
                  </Grid>

                  {PAYMENT_SYSTEM_LANG_FIELDS.map(({ field, label }) => (
                    <Grid item xs={12} sm={6} md={3} key={field}>
                      <TextField
                        label={t(label)}
                        value={system[field]}
                        onChange={handlePaymentSystemChange(index, field)}
                        error={Boolean(errors[field])}
                        helperText={errors[field] ? t('Обязательное поле') : ' '}
                        fullWidth
                      />
                    </Grid>
                  ))}

                  <Grid item xs={12}>
                    <TextField
                      label={t('Ссылка на изображение')}
                      value={system.image_url}
                      onChange={handlePaymentSystemChange(index, 'image_url')}
                      placeholder="https://example.com/payment-system.png"
                      fullWidth
                    />
                  </Grid>
                </Grid>
              </Paper>
            );
          })}

          <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
            <Button
              variant="contained"
              onClick={handleSavePaymentSystems}
              disabled={savingPaymentSystems || loadingPaymentSystems || hasPaymentSystemErrors || paymentSystems.length === 0}
            >
              {savingPaymentSystems ? t('Сохранение...') : t('Сохранить')}
            </Button>
          </Box>
        </Box>
      )}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
